import React, { useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useLogger } from "../services/logging";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import EventFormDialog, { EventFormValues } from "./EventFormDialog";
import PositionFormDialog, { PositionFormValues } from "./PositionFormDialog";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MDEditor from "@uiw/react-md-editor";
import { PositionType, type AverageCostBasisResult } from "../types/position";
import { ValidationResult } from "../types/validation";
import { useQuery, useLazyQuery } from "@apollo/client";
import { AVERAGE_COST_BASIS } from "../graphql/positions";
import useMarketQuoteAutoFill from "../hooks/useMarketQuoteAutoFill";
import { GET_ME } from "../graphql/users";
import { SEARCH_SYMBOLS } from "../graphql/symbols";
import { useValidationSummary, useWalletDeposit } from "../graphql/validation";
import TradeMarketContext from "./TradeMarketContext";
import BalanceChecker from "./BalanceChecker";
import ValidationStatusBar from "./ValidationStatusBar";

export interface TradeFormValues {
  id?: number;
  symbol: string;
  date: Date | null;
  side: string;
  notes?: string;
  walletId?: string;
  tradeFee?: number;
  positionFee?: number;
  events: EventFormValues[];
  positions: PositionFormValues[];
}

interface TradeFormDialogProps {
  open: boolean;
  initialValues?: TradeFormValues;
  onClose: () => void;
  onSave: (values: TradeFormValues) => Promise<{ id: number } | void>;
  onEventCreate: (options: { variables: { data: any } }) => Promise<any>;
  onEventUpdate: (options: {
    variables: { id: number; data: any };
  }) => Promise<any>;
  onEventDelete: (options: { variables: { id: number } }) => Promise<any>;
  onPositionCreate: (options: {
    variables: { createPositionInput: any };
  }) => Promise<any>;
  onPositionUpdate: (options: {
    variables: { id: string; updatePositionInput: any };
  }) => Promise<any>;
  onPositionDelete: (options: { variables: { id: string } }) => Promise<any>;
  onRefresh?: () => void;
  onFetchTrade?: (tradeId: number) => Promise<any>;
}

const sanitizeUpdatePositionInput = (position: PositionFormValues) => ({
  date:
    position.date instanceof Date ? position.date.toISOString() : position.date,
  notes: position.notes,
  price: position.price,
  quantity: position.quantity,
  status: position.status,
  type: position.type,
});

const sanitizeEventInput = (
  event: EventFormValues,
  overrideTradeId?: number,
) => {
  const sanitized: Record<string, any> = {
    date: event.date instanceof Date ? event.date.toISOString() : event.date,
    description: event.description,
    type: event.type,
    eventType: event.eventType,
    status: event.status,
    impact: event.impact,
    probability: event.probability,
    projectedIncrease: event.projectedIncrease,
    targetPrice: event.targetPrice,
    stopLoss: event.stopLoss,
  };

  const tradeIdToUse =
    overrideTradeId !== undefined
      ? overrideTradeId
      : event.tradeId !== undefined
        ? event.tradeId
        : undefined;

  if (tradeIdToUse !== undefined) {
    sanitized.tradeId = tradeIdToUse;
  }

  return sanitized;
};

const getFriendlyErrorMessage = (error: any) => {
  if (!error) return "Something went wrong. Please try again.";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  if (typeof error.message === "string") {
    return error.message;
  }
  return "Something went wrong. Please try again.";
};

const SIDES = [
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

const getPositionTypeColor = (type: PositionType) => {
  switch (type) {
    case "ENTRY":
      return "primary";
    case "EXIT":
      return "error";
    case "PARTIAL_EXIT":
      return "warning";
    case "ADD_TO_POSITION":
      return "info";
    default:
      return "default";
  }
};

export default function TradeFormDialog({
  open,
  initialValues,
  onClose,
  onSave,
  onEventCreate,
  onEventUpdate,
  onEventDelete,
  onPositionCreate,
  onPositionUpdate,
  onPositionDelete,
  onRefresh,
  onFetchTrade,
}: TradeFormDialogProps) {
  const logger = useLogger("TradeFormDialog");
  const { warn: logWarn } = logger;
  const [symbol, setSymbol] = useState(initialValues?.symbol || "");
  const [date, setDate] = useState<Date | null>(
    initialValues?.date || new Date(),
  );
  const [side, setSide] = useState(initialValues?.side || "buy");
  const [notes, setNotes] = useState<string | undefined>(
    initialValues?.notes || "",
  );
  const [events, setEvents] = useState<EventFormValues[]>(
    initialValues?.events || [],
  );
  const [positions, setPositions] = useState<PositionFormValues[]>(
    initialValues?.positions || [],
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTradeId, setCurrentTradeId] = useState<number | undefined>(
    initialValues?.id,
  );
  const [validationSummary, setValidationSummary] =
    useState<ValidationResult | null>(null);
  const { getValidationSummary } = useValidationSummary();
  const { depositToWallet } = useWalletDeposit();

  // Event dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEventIndex, setEditingEventIndex] = useState<number | null>(
    null,
  );

  // Position dialog state
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [editingPositionIndex, setEditingPositionIndex] = useState<
    number | null
  >(null);

  // Position form state for initial position
  const [initialQuantity, setInitialQuantity] = useState(0);
  const [initialPrice, setInitialPrice] = useState(0);
  const [tradeFee, setTradeFee] = useState<number | undefined>(undefined);
  const [positionFee, setPositionFee] = useState<number | undefined>(undefined);

  // Get user's default fee settings
  const { data: userData } = useQuery(GET_ME);

  // Symbol autocomplete state
  const [symbolOptions, setSymbolOptions] = useState<string[]>([]);
  const [searchSymbols, { loading: symbolsLoading, data: symbolsData }] =
    useLazyQuery(SEARCH_SYMBOLS);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    marketContext: fetchedMarketContext,
    autoFillPrice,
    isPriceEdited,
    markPriceEdited,
  } = useMarketQuoteAutoFill({
    symbol,
    open,
    enabled: !initialValues,
  });

  const getValidationSummaryRef = React.useRef(getValidationSummary);
  React.useEffect(() => {
    getValidationSummaryRef.current = getValidationSummary;
  }, [getValidationSummary]);

  const runValidationSummary = useCallback(
    async (targetTradeId?: number) => {
      if (!targetTradeId) return null;
      try {
        const summary = await getValidationSummaryRef.current(targetTradeId);
        setValidationSummary(summary);
        return summary;
      } catch (error) {
        logWarn(
          "Failed to fetch validation summary",
          { method: "runValidationSummary" },
          error,
        );
        return null;
      }
    },
    [logWarn],
  );

  const handleDeposit = useCallback(
    async (amount: number) => {
      const walletId = initialValues?.walletId;
      if (!walletId || !currentTradeId) {
        throw new Error("Unable to deposit without an associated wallet.");
      }

      await depositToWallet(amount, walletId, "Auto deposit to cover trade");
      await runValidationSummary(currentTradeId);
    },
    [
      currentTradeId,
      depositToWallet,
      initialValues?.walletId,
      runValidationSummary,
    ],
  );

  React.useEffect(() => {
    setCurrentTradeId(initialValues?.id);
  }, [initialValues?.id]);

  React.useEffect(() => {
    if (open && currentTradeId) {
      runValidationSummary(currentTradeId);
    }
  }, [open, currentTradeId, runValidationSummary]);

  // Debounced symbol search
  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (query.length >= 1) {
          searchSymbols({ variables: { query } });
        } else {
          setSymbolOptions([]);
        }
      }, 300);
    },
    [searchSymbols],
  );

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Update symbol options when search results arrive
  React.useEffect(() => {
    if (symbolsData?.searchSymbols) {
      setSymbolOptions(symbolsData.searchSymbols || []);
    }
  }, [symbolsData]);

  React.useEffect(() => {
    if (!initialValues && autoFillPrice != null && !isPriceEdited) {
      setInitialPrice(autoFillPrice);
    }
  }, [autoFillPrice, initialValues, isPriceEdited]);

  // Load default fees from user settings
  React.useEffect(() => {
    if (userData?.me && tradeFee === undefined && positionFee === undefined) {
      setTradeFee(userData.me.defaultTradeFee);
      setPositionFee(userData.me.defaultPositionFee);
    }
  }, [userData, tradeFee, positionFee]);

  // Confirmation dialogs state
  const [deletePositionDialogOpen, setDeletePositionDialogOpen] =
    useState(false);
  const [positionToDelete, setPositionToDelete] = useState<{
    index: number;
    position: PositionFormValues;
  } | null>(null);
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{
    index: number;
    event: EventFormValues;
  } | null>(null);

  // Update state when initialValues changes (for editing)
  React.useEffect(() => {
    logger.debug(
      "TradeFormDialog initialValues changed",
      { method: "useEffect" },
      { initialValues },
    );
    if (initialValues) {
      logger.debug(
        "Loading trade for editing",
        { method: "useEffect" },
        { initialValues },
      );
      setSymbol(initialValues.symbol);
      setDate(initialValues.date);
      setSide(initialValues.side);
      setNotes(initialValues.notes || "");
      setEvents(initialValues.events || []);
      setPositions(
        (initialValues.positions || []).map((pos) => ({
          ...pos,
          date: pos.date ? new Date(pos.date) : new Date(),
        })),
      );
      // Load fees from initial values if they exist
      if (initialValues.tradeFee !== undefined) {
        setTradeFee(initialValues.tradeFee);
      }
      if (initialValues.positionFee !== undefined) {
        setPositionFee(initialValues.positionFee);
      }
      logger.debug(
        "Set positions for editing",
        { method: "useEffect" },
        { positions: initialValues.positions },
      );
    } else {
      logger.debug("Resetting form for new trade", { method: "useEffect" });
      // Reset for new trade
      setSymbol("");
      setDate(new Date());
      setSide("buy");
      setNotes("");
      setEvents([]);
      setPositions([]);
      setInitialQuantity(0);
      setInitialPrice(0);
      // Reset fees to user defaults
      setTradeFee(userData?.me?.defaultTradeFee);
      setPositionFee(userData?.me?.defaultPositionFee);
    }
  }, [initialValues, userData]);

  // Initialize first position if creating new trade (only when not editing)
  React.useEffect(() => {
    if (!initialValues && positions.length === 0) {
      logger.debug(
        "Creating initial position",
        { method: "useEffect" },
        { initialQuantity, initialPrice },
      );
      setPositions([
        {
          type: "ENTRY",
          quantity: initialQuantity,
          price: initialPrice,
          date: date,
          notes: "Initial entry position",
          status: "OPEN",
        },
      ]);
    }
  }, [initialValues, positions.length, initialQuantity, initialPrice, date]);

  // Update the first position when initial quantity/price changes (only when creating new trade)
  React.useEffect(() => {
    if (
      !initialValues &&
      positions.length > 0 &&
      positions[0]?.type === "ENTRY"
    ) {
      if (
        positions[0].quantity !== initialQuantity ||
        positions[0].price !== initialPrice
      ) {
        setPositions((prev) =>
          prev.map((pos, idx) =>
            idx === 0
              ? { ...pos, quantity: initialQuantity, price: initialPrice }
              : pos,
          ),
        );
      }
    }
  }, [initialValues, initialQuantity, initialPrice]);

  const handleAddEvent = () => {
    setEditingEventIndex(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (idx: number) => {
    setEditingEventIndex(idx);
    setEventDialogOpen(true);
  };

  const handleDeleteEventClick = (idx: number) => {
    setEventToDelete({ index: idx, event: events[idx] });
    setDeleteEventDialogOpen(true);
  };

  const handleDeleteEventConfirm = async () => {
    if (!eventToDelete) return;

    const { index, event } = eventToDelete;

    // If the event has an ID, delete it from the backend immediately
    if (event.id) {
      try {
        await onEventDelete({ variables: { id: event.id } });
      } catch (error) {
        logger.error(
          "Error deleting event",
          { method: "handleEventDelete" },
          error,
        );
        alert("Failed to delete event. Please try again.");
        setDeleteEventDialogOpen(false);
        setEventToDelete(null);
        return;
      }
    }

    // Remove from local state
    setEvents(events.filter((_, i) => i !== index));
    setDeleteEventDialogOpen(false);
    setEventToDelete(null);
  };

  const handleDeleteEventCancel = () => {
    setDeleteEventDialogOpen(false);
    setEventToDelete(null);
  };

  const handleEventSave = (event: EventFormValues) => {
    if (editingEventIndex === null) {
      setEvents([...events, event]);
    } else {
      setEvents(events.map((e, i) => (i === editingEventIndex ? event : e)));
    }
    setEventDialogOpen(false);
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
  };

  const handleAddPosition = () => {
    setEditingPositionIndex(null);
    setPositionDialogOpen(true);
  };

  const handleEditPosition = (idx: number) => {
    setEditingPositionIndex(idx);
    setPositionDialogOpen(true);
  };

  const handleDeletePositionClick = (idx: number) => {
    // Don't allow deleting the first position when creating a new trade
    if (!initialValues && idx === 0) {
      return;
    }

    // Don't allow deleting if there's only one position
    if (positions.length <= 1) {
      alert(
        "Cannot delete the last position. A trade must have at least one position.",
      );
      return;
    }

    setPositionToDelete({ index: idx, position: positions[idx] });
    setDeletePositionDialogOpen(true);
  };

  const handleDeletePositionConfirm = async () => {
    if (!positionToDelete) return;

    const { index, position } = positionToDelete;

    // If the position has an ID, delete it from the backend immediately
    if (position.id) {
      try {
        await onPositionDelete({ variables: { id: position.id } });
      } catch (error: any) {
        logger.error(
          "Error deleting position",
          { method: "handlePositionDelete" },
          error,
        );
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          "Failed to delete position. Please try again.";
        alert(errorMessage);
        setDeletePositionDialogOpen(false);
        setPositionToDelete(null);
        return;
      }
    }

    // Remove from local state
    setPositions(positions.filter((_, i) => i !== index));
    setDeletePositionDialogOpen(false);
    setPositionToDelete(null);

    // If we're editing a trade and have a fetch function, refresh the trade data
    if (initialValues?.id && onFetchTrade) {
      try {
        const updatedTrade = await onFetchTrade(initialValues.id);
        if (updatedTrade?.positions) {
          setPositions(
            updatedTrade.positions.map((pos: any) => ({
              ...pos,
              date: pos.date ? new Date(pos.date) : new Date(),
            })),
          );
        }
      } catch (error) {
        logger.error(
          "Error fetching updated trade data",
          { method: "handleRefresh" },
          error,
        );
      }
    }

    // Also refresh the main table data
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleDeletePositionCancel = () => {
    setDeletePositionDialogOpen(false);
    setPositionToDelete(null);
  };

  const handlePositionSave = (position: PositionFormValues) => {
    logger.debug(
      "Saving position",
      { method: "handlePositionSave" },
      { position },
    );
    if (editingPositionIndex === null) {
      logger.debug("Adding new position to positions array", {
        method: "handlePositionSave",
      });
      setPositions([...positions, position]);
    } else {
      logger.debug(
        "Updating position at index",
        { method: "handlePositionSave" },
        { editingPositionIndex },
      );
      setPositions(
        positions.map((p, i) => (i === editingPositionIndex ? position : p)),
      );
    }
    setPositionDialogOpen(false);
  };

  const handlePositionDialogClose = () => {
    setPositionDialogOpen(false);
  };

  const resetFormState = () => {
    setSymbol("");
    setDate(new Date());
    setSide("buy");
    setNotes("");
    setTradeFee(userData?.me?.defaultTradeFee);
    setPositionFee(userData?.me?.defaultPositionFee);
    setEvents([]);
    setPositions([]);
    setInitialQuantity(0);
    setInitialPrice(0);
    setSaveError(null);
    setValidationSummary(null);
    setCurrentTradeId(undefined);
  };

  const isTradeClosed = (summary?: ValidationResult | null) =>
    summary?.errors?.some((error) => /closed trade/i.test(error || ""));

  const handleSave = async () => {
    logger.debug(
      "handleSave called with positions",
      { method: "handleSave" },
      { positions },
    );

    if (!symbol || !date || !side) {
      setSaveError("Symbol, date, and side are required.");
      return;
    }

    setSaveError(null);
    setIsSubmitting(true);
    let createdTradeId: number | undefined;

    try {
      const tradeData = {
        symbol,
        date,
        side,
        notes,
        tradeFee: tradeFee || undefined,
        positionFee: positionFee || undefined,
        events,
        positions,
        walletId: undefined,
      };

      logger.debug(
        "Saving trade with data",
        { method: "handleSave" },
        { tradeData },
      );

      const tradeResult = await onSave(tradeData);
      logger.debug(
        "Trade save result",
        { method: "handleSave" },
        { tradeResult },
      );

      if (!tradeResult || !("id" in tradeResult)) {
        throw new Error("Trade save failed. Please try again.");
      }

      createdTradeId = tradeResult.id;
      setCurrentTradeId(createdTradeId);

      const latestSummary = await runValidationSummary(createdTradeId);
      if (isTradeClosed(latestSummary)) {
        throw new Error(
          latestSummary?.errors?.join(". ") ||
            "Cannot add positions to a closed trade",
        );
      }

      const initialEvents = initialValues?.events || [];
      const currentEvents = events;

      for (const event of currentEvents) {
        if (!event.id) {
          await onEventCreate({
            variables: {
              data: sanitizeEventInput(event, createdTradeId),
            },
          });
        }
      }

      for (const event of currentEvents) {
        if (event.id && initialEvents.find((e) => e.id === event.id)) {
          await onEventUpdate({
            variables: {
              id: event.id,
              data: sanitizeEventInput(event, createdTradeId),
            },
          });
        }
      }

      const initialPositions = initialValues?.positions || [];
      const currentPositions = positions;

      for (const position of currentPositions) {
        if (!position.id) {
          await onPositionCreate({
            variables: {
              createPositionInput: { ...position, tradeId: createdTradeId },
            },
          });
        }
      }

      for (const position of currentPositions) {
        if (position.id && initialPositions.find((p) => p.id === position.id)) {
          await onPositionUpdate({
            variables: {
              id: position.id,
              updatePositionInput: sanitizeUpdatePositionInput(position),
            },
          });
        }
      }

      if (onRefresh) {
        try {
          await onRefresh();
        } catch (error: any) {
          logger.error(
            "Error refreshing data after trade save",
            { method: "handleSave" },
            error,
          );
        }
      }

      if (!initialValues) {
        resetFormState();
      }

      onClose();
    } catch (error: any) {
      const friendlyMessage = getFriendlyErrorMessage(error);
      setSaveError(friendlyMessage);
      logger.error("Trade save failed", { method: "handleSave" }, error);
      if (createdTradeId) {
        await runValidationSummary(createdTradeId);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to close all dialogs and reset focus
  const handleCloseAllDialogs = () => {
    setDeletePositionDialogOpen(false);
    setDeleteEventDialogOpen(false);
    setPositionDialogOpen(false);
    setEventDialogOpen(false);
    setSaveError(null);
    setValidationSummary(null);
    setCurrentTradeId(initialValues?.id);
    onClose();
  };

  const eventDialogInitialValues =
    editingEventIndex !== null
      ? {
          ...events[editingEventIndex],
          date: events[editingEventIndex]?.date
            ? new Date(events[editingEventIndex].date as any)
            : new Date(),
          eventType: events[editingEventIndex]?.eventType
            ? events[editingEventIndex].eventType
            : undefined,
          status: events[editingEventIndex]?.status
            ? events[editingEventIndex].status
            : undefined,
          impact: events[editingEventIndex]?.impact
            ? events[editingEventIndex].impact
            : undefined,
          probability: events[editingEventIndex]?.probability ?? undefined,
          projectedIncrease:
            events[editingEventIndex]?.projectedIncrease ?? undefined,
          targetPrice: events[editingEventIndex]?.targetPrice ?? undefined,
          stopLoss: events[editingEventIndex]?.stopLoss ?? undefined,
          description: events[editingEventIndex]?.description || "",
          type: events[editingEventIndex]?.type || "",
        }
      : undefined;
  // if (eventDialogOpen) {
  //   logger.debug('EventFormDialog opened with initialValues', { method: 'render' }, { eventDialogInitialValues });
  // }

  const entryPosition =
    positions.find((position) => position.type === "ENTRY") || positions[0];
  const entryQuantity = entryPosition?.quantity ?? 0;
  const entryPrice = entryPosition?.price ?? 0;
  const showBalanceChecker = Boolean(
    currentTradeId && entryQuantity > 0 && entryPrice > 0,
  );

  const tradeId = initialValues?.id;
  const { data: acbData, loading: acbLoading } = useQuery<{
    averageCostBasis: AverageCostBasisResult;
  }>(AVERAGE_COST_BASIS, { skip: !tradeId, variables: { tradeId } });

  return (
    <Dialog open={open} onClose={handleCloseAllDialogs} maxWidth="md" fullWidth>
      <DialogTitle>{initialValues ? "Edit Trade" : "Add Trade"}</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2}>
          {/* Trade Details */}
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              Trade Details
            </Typography>
            <Autocomplete
              freeSolo
              fullWidth
              options={symbolOptions}
              value={symbol}
              loading={symbolsLoading}
              onInputChange={(event, newInputValue) => {
                // Update symbol state immediately when typing
                setSymbol(newInputValue);
                debouncedSearch(newInputValue);
              }}
              onChange={(event, newValue) => {
                setSymbol(newValue || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Symbol"
                  margin="normal"
                  helperText="Start typing to search for symbols"
                />
              )}
            />

            {/* Market Context Display */}
            {symbol && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <TradeMarketContext
                  trade={{
                    symbol,
                    marketContext:
                      fetchedMarketContext || initialValues?.marketContext,
                  }}
                />
              </Box>
            )}
            <DatePicker
              label="Date"
              value={date}
              onChange={setDate}
              slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
            />
            <TextField
              select
              label="Side"
              value={side}
              onChange={(e) => setSide(e.target.value)}
              fullWidth
              margin="normal"
            >
              {SIDES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {!initialValues && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Initial Position
                </Typography>
                <TextField
                  label="Quantity"
                  type="number"
                  value={initialQuantity}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    logger.debug(
                      "Setting initialQuantity",
                      { method: "onChange" },
                      { value },
                    );
                    setInitialQuantity(value);
                  }}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Price"
                  type="number"
                  inputProps={{ step: 0.01 }}
                  value={initialPrice}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    logger.debug(
                      "Setting initialPrice",
                      { method: "onChange" },
                      { value },
                    );
                    setInitialPrice(value);
                    markPriceEdited();
                  }}
                  fullWidth
                  margin="normal"
                />
              </>
            )}

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Notes (Markdown supported)
            </Typography>
            <Box sx={{ mt: 1, mb: 2 }}>
              <MDEditor value={notes} onChange={setNotes} height={200} />
            </Box>

            {/* Fee Information */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Fee Information
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Configure fees for this trade. Leave empty to use default user
                settings.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <TextField
                  label="Trade Fee (%)"
                  type="number"
                  value={tradeFee === undefined ? "" : tradeFee}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradeFee(value === "" ? undefined : Number(value));
                  }}
                  fullWidth
                  size="small"
                  inputProps={{
                    step: 0.01,
                    min: 0,
                    max: 100,
                  }}
                  helperText="Override default trade fee"
                />
                <TextField
                  label="Position Fee (%)"
                  type="number"
                  value={positionFee === undefined ? "" : positionFee}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPositionFee(value === "" ? undefined : Number(value));
                  }}
                  fullWidth
                  size="small"
                  inputProps={{
                    step: 0.01,
                    min: 0,
                    max: 100,
                  }}
                  helperText="Override default position fee"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                • Fees are automatically deducted from wallet balance
              </Typography>
            </Box>
          </Box>

          {/* Positions and Events */}
          <Box flex={1}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {saveError}
              </Alert>
            )}
            {validationSummary && (
              <ValidationStatusBar validation={validationSummary} />
            )}
            {showBalanceChecker && (
              <BalanceChecker
                tradeId={currentTradeId}
                quantity={Math.max(entryQuantity, 0)}
                price={entryPrice}
                onDeposit={initialValues?.walletId ? handleDeposit : undefined}
              />
            )}
            {/* Positions */}
            <Typography variant="h6" gutterBottom>
              Positions
            </Typography>
            <List dense>
              {positions.map((position, idx) => (
                <ListItem key={position.id ?? idx}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={position.type}
                          color={getPositionTypeColor(position.type)}
                          size="small"
                        />
                        <Typography variant="body2">
                          {position.quantity} @ ${position.price}
                        </Typography>
                        {idx === 0 && tradeId && (
                          <Box ml={2}>
                            {acbLoading ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Loading ACB...
                              </Typography>
                            ) : (
                              acbData?.averageCostBasis && (
                                <Box mt={1} data-testid="acb-display">
                                  <Typography variant="body2" component="span">
                                    Avg Cost: $
                                    {acbData.averageCostBasis.averageCostBasis.toFixed(
                                      2,
                                    )}
                                  </Typography>
                                  <Typography variant="body2" component="span">
                                    Qty:{" "}
                                    {acbData.averageCostBasis.totalQuantity}
                                  </Typography>
                                  <Typography variant="body2" component="span">
                                    Total: $
                                    {acbData.averageCostBasis.totalCost.toFixed(
                                      2,
                                    )}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption">
                          {position.date
                            ? typeof position.date === "string"
                              ? new Date(position.date).toLocaleDateString()
                              : position.date.toLocaleDateString()
                            : "No date"}{" "}
                          - {position.notes || "No notes"}
                        </Typography>
                      </>
                    }
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      edge="end"
                      onClick={() => handleEditPosition(idx)}
                    >
                      <EditIcon />
                    </IconButton>
                    {!(initialValues === undefined && idx === 0) &&
                      positions.length > 1 && (
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDeletePositionClick(idx)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                  </Box>
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              onClick={handleAddPosition}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
            >
              Add Position
            </Button>

            {/* Events */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Events
            </Typography>
            <List dense>
              {events.map((event, idx) => (
                <ListItem key={event.id ?? idx} alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {event.eventType || event.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.status || ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.impact || ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.date
                            ? typeof event.date === "string"
                              ? new Date(event.date).toLocaleDateString()
                              : event.date.toLocaleDateString()
                            : "No date"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      event.description ? (
                        <Typography variant="caption" component="span">
                          {event.description}
                        </Typography>
                      ) : null
                    }
                  />
                  <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                    {event.probability !== undefined && (
                      <Chip
                        label={`Prob: ${event.probability}%`}
                        size="small"
                      />
                    )}
                    {event.projectedIncrease !== undefined && (
                      <Chip
                        label={`Proj: ${event.projectedIncrease}%`}
                        size="small"
                      />
                    )}
                    {event.targetPrice !== undefined && (
                      <Chip
                        label={`Target: $${event.targetPrice}`}
                        size="small"
                      />
                    )}
                    {event.stopLoss !== undefined && (
                      <Chip label={`Stop: $${event.stopLoss}`} size="small" />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton edge="end" onClick={() => handleEditEvent(idx)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleDeleteEventClick(idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
            <Button variant="outlined" onClick={handleAddEvent} sx={{ mt: 1 }}>
              Add Event
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAllDialogs} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>

      <EventFormDialog
        open={eventDialogOpen}
        initialValues={eventDialogInitialValues}
        onClose={handleEventDialogClose}
        onSave={handleEventSave}
      />

      <PositionFormDialog
        open={positionDialogOpen}
        initialValues={
          editingPositionIndex !== null
            ? positions[editingPositionIndex]
            : undefined
        }
        onClose={handlePositionDialogClose}
        onSave={handlePositionSave}
        tradeId={tradeId}
        symbol={symbol}
        marketContext={fetchedMarketContext}
      />

      {/* Position Delete Confirmation Dialog */}
      <Dialog
        open={deletePositionDialogOpen}
        onClose={handleDeletePositionCancel}
        aria-labelledby="delete-position-dialog-title"
        aria-describedby="delete-position-dialog-description"
      >
        <DialogTitle id="delete-position-dialog-title">
          Delete Position
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-position-dialog-description">
            Are you sure you want to delete this position? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletePositionCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeletePositionConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Delete Confirmation Dialog */}
      <Dialog
        open={deleteEventDialogOpen}
        onClose={handleDeleteEventCancel}
        aria-labelledby="delete-event-dialog-title"
        aria-describedby="delete-event-dialog-description"
      >
        <DialogTitle id="delete-event-dialog-title">Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-event-dialog-description">
            Are you sure you want to delete this event? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteEventCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEventConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
